import { apiService } from "../services/api";
import type { AppDispatch } from "../state/store";
import { showAlertDialog } from "../state/utils/confirmationDialogUtils";

export const PURCHASE_RETURN_PRIOR_WARNING_TITLE = "Warning";

const ROUND_OFF_EPSILON = 0.009;

export type PriorPurchaseReturnEntry = {
  return_no?: string;
  return_date?: string;
  document_amount?: number | string;
  returned_amount?: number | string;
};

export type PurchaseReturnPurchaseWarning = {
  vouchers: PriorPurchaseReturnEntry[];
  has_round_off: boolean;
  round_off_amt: number;
  purchase_no: string;
  message: string;
  round_off_message: string;
};

function formatPriorReturnWarningDate(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const datePart = raw.split("T", 1)[0].split(" ", 1)[0];
  const [year, month, day] = datePart.split("-");
  if (year?.length === 4 && month && day) {
    return `${day}/${month}/${year}`;
  }
  return raw;
}

function formatPriorReturnLine(row: PriorPurchaseReturnEntry): string {
  const returnNo = String(row.return_no || "—").trim();
  const returnDate = formatPriorReturnWarningDate(row.return_date);
  const documentAmount = parseFloat(String(row.document_amount ?? 0));
  let detail = ` • ${returnNo}`;
  if (returnDate) detail += ` — ${returnDate}`;
  if (!Number.isNaN(documentAmount) && documentAmount > 0) {
    detail += ` — ${documentAmount.toFixed(2)}`;
  }
  return detail;
}

function parseRoundOffAmount(value: unknown): number {
  const amount = parseFloat(String(value ?? 0));
  return Number.isNaN(amount) ? 0 : amount;
}

export function purchaseHasRoundOff(roundOffAmt: unknown): boolean {
  return Math.abs(parseRoundOffAmount(roundOffAmt)) > ROUND_OFF_EPSILON;
}

function formatPriorPurchaseReturnMessage(
  vouchers: PriorPurchaseReturnEntry[],
  purchaseNo = ""
): string {
  if (vouchers.length === 0) return "";
  const header = purchaseNo
    ? `Prior purchase return entries exist for this purchase (${purchaseNo}):`
    : "Prior purchase return entries exist for this purchase:";
  const lines = [
    header,
    ...vouchers.slice(0, 5).map((row) => formatPriorReturnLine(row)),
  ];
  const extra = vouchers.length - 5;
  if (extra > 0) lines.push(` (+${extra} more)`);
  const total = vouchers.reduce(
    (sum, row) => sum + (parseFloat(String(row.returned_amount ?? 0)) || 0),
    0
  );
  if (total > 0) lines.push(`Total returned so far: ${total.toFixed(2)}`);
  lines.push(
    "Please verify return quantities to avoid duplicate or excess returns."
  );
  return lines.join("\n");
}

function formatRoundOffMessage(roundOffAmt: number, purchaseNo = ""): string {
  if (!purchaseHasRoundOff(roundOffAmt)) return "";
  const header = purchaseNo
    ? `Selected purchase has a round off value (${purchaseNo}): ${roundOffAmt.toFixed(2)}`
    : `Selected purchase has a round off value: ${roundOffAmt.toFixed(2)}`;
  return [
    header,
    "Please verify return amounts carefully — round off is not included in line-level returns.",
  ].join("\n");
}

export const isPurchaseReturnAddContext = (
  screenId?: string | null,
  moduleEndpoint?: string | null
): boolean => {
  const sid = String(screenId || "").trim();
  const module = String(moduleEndpoint || "").trim();
  if (module === "pur_ret_add") return true;
  if (sid === "126" || sid === "18") return true;
  return false;
};

export async function fetchPurchaseReturnPurchaseWarning(
  purchaseId: string,
  excludeReturnId?: string
): Promise<PurchaseReturnPurchaseWarning> {
  const exclude = excludeReturnId
    ? `?exclude_id=${encodeURIComponent(excludeReturnId)}`
    : "";
  const response = await apiService.get<{
    success?: boolean;
    vouchers?: PriorPurchaseReturnEntry[];
    has_round_off?: boolean;
    round_off_amt?: number | string;
    purchase_no?: string;
    message?: string;
    round_off_message?: string;
  }>(
    `/pabws/module/pur_ret_add/prior_returns/${encodeURIComponent(
      purchaseId
    )}${exclude}`
  );
  const vouchers = Array.isArray(response?.vouchers) ? response.vouchers : [];
  const roundOffAmt = parseRoundOffAmount(response?.round_off_amt);
  const purchaseNo = String(response?.purchase_no || "").trim();
  const hasRoundOff =
    typeof response?.has_round_off === "boolean"
      ? response.has_round_off
      : purchaseHasRoundOff(roundOffAmt);
  const priorMessage = formatPriorPurchaseReturnMessage(vouchers, purchaseNo);
  const roundOffMessage =
    String(response?.round_off_message || "").trim() ||
    formatRoundOffMessage(roundOffAmt, purchaseNo);
  const combinedFromApi = String(response?.message || "").trim();
  const message =
    combinedFromApi ||
    [priorMessage, hasRoundOff ? roundOffMessage : ""]
      .filter(Boolean)
      .join("\n\n");

  return {
    vouchers,
    has_round_off: hasRoundOff,
    round_off_amt: roundOffAmt,
    purchase_no: purchaseNo,
    message,
    round_off_message: hasRoundOff ? roundOffMessage : "",
  };
}

export async function fetchPriorPurchaseReturnsForPurchase(
  purchaseId: string,
  excludeReturnId?: string
): Promise<PriorPurchaseReturnEntry[]> {
  const warning = await fetchPurchaseReturnPurchaseWarning(
    purchaseId,
    excludeReturnId
  );
  return warning.vouchers;
}

export async function showPurchaseReturnPriorWarningAlert(
  dispatch: AppDispatch,
  message: string
): Promise<void> {
  await showAlertDialog(dispatch, message, PURCHASE_RETURN_PRIOR_WARNING_TITLE);
}

export async function triggerPurchaseReturnPriorWarning(
  dispatch: AppDispatch,
  purchaseId: string,
  excludeReturnId: string | undefined,
  cacheRef: { current: string }
): Promise<void> {
  const pid = String(purchaseId || "").trim();
  if (!pid) return;
  // Key only on purchase — excludeReturnId changes after save and must not re-alert.
  const cacheKey = `pr:${pid}`;
  if (cacheRef.current === cacheKey) return;
  cacheRef.current = cacheKey;

  try {
    const warning = await fetchPurchaseReturnPurchaseWarning(
      pid,
      excludeReturnId
    );
    const hasPrior = warning.vouchers.length > 0;
    if (!hasPrior && !warning.has_round_off) return;

    const message =
      warning.message ||
      [
        formatPriorPurchaseReturnMessage(warning.vouchers, warning.purchase_no),
        warning.round_off_message,
      ]
        .filter(Boolean)
        .join("\n\n");
    if (message) {
      await showPurchaseReturnPriorWarningAlert(dispatch, message);
    }
  } catch {
    cacheRef.current = "";
  }
}
